import React, { useState, useEffect, useRef } from "react";
import ReactCountryFlag from "react-country-flag";
import { getTranslation } from "../../service/service.js";
import { UI_TRANSLATIONS } from "../../lib/i18n";
import styles from "./GlobalLanguageSwitcher.module.css";

/**
 * Idiomas sempre disponÃ­veis no seletor de UI.
 * Independente do perfil do curso do usuÃ¡rio.
 * Adicione ou remova idiomas aqui para ajustar a oferta.
 */
const UI_LANGUAGES = [
  { id: "pt", flag: "br", name: "Português" },
  { id: "en", flag: "us", name: "English"   },
  { id: "es", flag: "es", name: "Español"   },
];

/**
 * Retorna o cÃ³digo exato que a API TaaS aceita para um idioma de UI,
 * respeitando a lÃ³gica de region_code para usuÃ¡rios nÃ£o-BRA
 * (igual ao comportamento de selectedLanguage.js).
 */
function getApiCode(uiLangId) {
  try {
    const user = JSON.parse(localStorage.getItem("user-info")) || { data: {} };
    const data = user.data || {};
    const available = data.available_languages || [];
    const region = data.region_user;
    const regionCode = data.region_code;
    const id = uiLangId.toLowerCase();

    // Comportamento igual ao selectedLanguage.js: nÃ£o-BRA + inglÃªs â†’ usa region_code
    if (region && region !== "BRA" && id === "en" && regionCode) {
      return regionCode;
    }

    const found = available.find(function(l) {
      const lc = l.code.toLowerCase();
      return lc === id || lc.startsWith(id + "-") || id.startsWith(lc + "-");
    });
    return found ? found.code : uiLangId;
  } catch (e) {
    return uiLangId;
  }
}

/**
 * Retorna o id de UI correspondente ao cÃ³digo armazenado no languagePack.
 * Ex: "pt-br" â†’ "pt", "en-us" â†’ "en"
 */
function toUiId(code) {
  if (!code) return null;
  const lc = code.toLowerCase();
  for (let i = 0; i < UI_LANGUAGES.length; i++) {
    const id = UI_LANGUAGES[i].id;
    if (lc === id || lc.startsWith(id + "-") || id.startsWith(lc + "-")) {
      return id;
    }
  }
  return code;
}

/**
 * Detecta o idioma atual alinhado com o comportamento do app:
 * 1. languagePack.selectedLanguage (jÃ¡ setado pelo usuÃ¡rio ou login)
 * 2. available_languages[0].code (primeiro idioma do perfil)
 * 3. "en" â€” mesmo fallback de home.js getCurrentLanguage()
 */
function detectCurrentLanguageId() {
  try {
    const pack = JSON.parse(localStorage.getItem("languagePack"));
    if (pack && pack.uiLanguage) {
      return toUiId(pack.uiLanguage);
    }
    if (pack && pack.selectedLanguage) {
      return toUiId(pack.selectedLanguage);
    }
    const user = JSON.parse(localStorage.getItem("user-info"));
    const available = user && user.data && user.data.available_languages;
    if (available && available.length > 0) {
      return toUiId(available[0].code);
    }
  } catch (e) { /* ignore */ }
  return "en"; // fallback idÃªntico ao home.js
}

const GlobalLanguageSwitcher = function() {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [currentId, setCurrentId] = useState(detectCurrentLanguageId);
  const wrapperRef = useRef(null);

  useEffect(function() {
    const handleClickOutside = function(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return function() { document.removeEventListener("mousedown", handleClickOutside); };
  }, []);

  const currentLang = UI_LANGUAGES.find(function(l) { return l.id === currentId; }) || UI_LANGUAGES[0];

  const handleSelect = function(lang) {
    if (lang.id === currentId || loading) return;
    setOpen(false);
    setLoading(true);

    const apiCode = getApiCode(lang.id);

    getTranslation(apiCode)
      .then(function(res) {
        let pack = {};
        try { pack = JSON.parse(localStorage.getItem("languagePack") || "{}"); } catch(e) {}
        const fallback = UI_TRANSLATIONS[lang.id] || {};
        const updated = Object.assign({}, pack, fallback, res.data, {
          uiLanguage: lang.id,
        });
        localStorage.setItem("languagePack", JSON.stringify(updated));
        window.location.reload();
      })
      .catch(function() {
        // A TaaS pode não ter tradução para este idioma (ex: PT é o padrão do app).
        // Salva fallback hardcoded + uiLanguage; preserva selectedLanguage/languageCode do curso.
        let packFallback = {};
        try { packFallback = JSON.parse(localStorage.getItem("languagePack") || "{}"); } catch(e) {}
        const fallbackMap = UI_TRANSLATIONS[lang.id] || {};
        localStorage.setItem("languagePack", JSON.stringify(
          Object.assign({}, packFallback, fallbackMap, { uiLanguage: lang.id })
        ));
        window.location.reload();
      });
  };

  const handleKeyDown = function(lang, e) {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleSelect(lang);
    }
  };

  return (
    <div className={styles.wrapper} ref={wrapperRef}>
      <button
        type="button"
        className={styles.trigger + (loading ? " " + styles.triggerDisabled : "")}
        onClick={function() { if (!loading) setOpen(function(p) { return !p; }); }}
        title="Trocar idioma"
        disabled={loading}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        {loading ? (
          <span className={styles.spinner} />
        ) : (
          <React.Fragment>
            <ReactCountryFlag
              svg
              countryCode={currentLang.flag}
              className={styles.flag}
            />
            <span>{currentLang.name}</span>
            <span className={styles.chevron + (open ? " " + styles.chevronOpen : "")} />
          </React.Fragment>
        )}
      </button>

      {open && (
        <div className={styles.dropdown} role="menu" aria-label="Seletor de idioma">
          {UI_LANGUAGES.map(function(lang) {
            const isActive = lang.id === currentId;
            return (
              <div
                key={lang.id}
                role="menuitem"
                tabIndex={0}
                className={styles.dropdownItem + (isActive ? " " + styles.dropdownItemActive : "")}
                onClick={function() { handleSelect(lang); }}
                onKeyDown={function(e) { handleKeyDown(lang, e); }}
              >
                <ReactCountryFlag
                  svg
                  countryCode={lang.flag}
                  className={styles.itemFlag}
                />
                <span className={styles.itemName}>{lang.name}</span>
                {isActive && <span className={styles.checkmark} />}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default GlobalLanguageSwitcher;
